import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal } from '../components/ui';
import { formatBytes } from '../lib/format';
import { errorText } from '../lib/labels';
import { uploadFiles } from './files';
import { RequestError, type ChooserRequest, type ViewerSocket } from './socket';

/** Mirrors the server default (MAX_UPLOAD_MB); the server is the authority. */
export const UPLOAD_LIMIT_BYTES = 20 * 1024 * 1024;

export interface FileChooserModalProps {
  chooser?: ChooserRequest;
  progress?: { chooserId: string; receivedBytes: number };
  socket: ViewerSocket;
}

type Phase = 'idle' | 'uploading' | 'finishing' | 'done';

/**
 * Shown to the controller when the remote page opens a file picker. The native picker on *this*
 * machine can only be opened from a click, hence the explicit button.
 */
export function FileChooserModal({ chooser, progress, socket }: FileChooserModalProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [sent, setSent] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    // new chooser -> reset
    setPhase('idle');
    setError(null);
    setFiles([]);
    setSent(0);
    cancelled.current = false;
  }, [chooser?.chooserId]);

  if (!chooser) return null;
  const total = files.reduce((n, f) => n + f.size, 0);

  const cancel = () => {
    cancelled.current = true;
    socket.send({ t: 'file.cancel', tabId: chooser.tabId, chooserId: chooser.chooserId });
    socket.clearChooser();
  };

  const start = async (picked: File[]) => {
    setError(null);
    if (picked.length === 0) return;
    if (!chooser.multiple && picked.length > 1) {
      setError(errorText('single_file'));
      return;
    }
    const size = picked.reduce((n, f) => n + f.size, 0);
    if (size > UPLOAD_LIMIT_BYTES) {
      setError(`${errorText('too_large')} (최대 ${formatBytes(UPLOAD_LIMIT_BYTES)})`);
      return;
    }
    setFiles(picked);
    setPhase('uploading');
    try {
      await socket.request({
        t: 'file.begin',
        tabId: chooser.tabId,
        chooserId: chooser.chooserId,
        files: picked.map((f) => ({
          name: f.name,
          size: f.size,
          mime: f.type || 'application/octet-stream',
        })),
      });
      await uploadFiles({
        files: picked,
        chooserId: chooser.chooserId,
        transport: {
          sendBinary: (b) => socket.sendBinary(b),
          bufferedAmount: () => socket.bufferedAmount,
        },
        cancelled: () => cancelled.current,
        onProgress: (n) => setSent(n),
      });
      setPhase('finishing');
      await socket.request({ t: 'file.end', tabId: chooser.tabId, chooserId: chooser.chooserId });
      setPhase('done');
      socket.clearChooser();
    } catch (err) {
      if (cancelled.current) return;
      setPhase('idle');
      setError(
        err instanceof RequestError ? errorText(err.code, err.message) : errorText('internal'),
      );
    }
  };

  const busy = phase === 'uploading' || phase === 'finishing';
  const received = progress?.chooserId === chooser.chooserId ? progress.receivedBytes : 0;
  const pct = total > 0 ? Math.min(100, Math.round((Math.max(sent, received) / total) * 100)) : 0;

  return (
    <Modal
      open
      title="페이지가 파일을 요청합니다"
      onClose={cancel}
      size="sm"
      footer={
        <>
          <Button onClick={cancel}>취소</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() => inputRef.current?.click()}
            disabled={busy}
          >
            {chooser.multiple ? '파일 선택 (여러 개 가능)' : '파일 선택'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-ink">
          원격 브라우저의 페이지가 파일 업로드를 요청했습니다. 이 PC의 파일을 골라 전달합니다.
          {chooser.multiple
            ? ' 여러 파일을 선택할 수 있습니다.'
            : ' 파일 하나만 선택할 수 있습니다.'}
        </p>
        <p className="text-xs text-ink-muted">
          최대 {formatBytes(UPLOAD_LIMIT_BYTES)}까지 전송할 수 있습니다.
        </p>
        {error && <Alert level="error">{error}</Alert>}
        {files.length > 0 && (
          <ul className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-line bg-canvas p-2 text-xs">
            {files.map((f) => (
              <li key={`${f.name}-${f.size}`} className="flex justify-between gap-2">
                <span className="truncate">{f.name}</span>
                <span className="shrink-0 text-ink-muted">{formatBytes(f.size)}</span>
              </li>
            ))}
          </ul>
        )}
        {busy && (
          <div>
            <div className="h-2 overflow-hidden rounded-full bg-canvas">
              <div className="h-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-1 text-xs text-ink-muted">
              {phase === 'finishing'
                ? '원격 페이지에 파일을 넣는 중…'
                : `전송 중 ${formatBytes(Math.max(sent, received))} / ${formatBytes(total)}`}
            </p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple={chooser.multiple}
          className="hidden"
          onChange={(e) => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';
            void start(picked);
          }}
        />
      </div>
    </Modal>
  );
}
